import type { Caption } from '@remotion/captions';

import type {
  CaptionTranslationSlot,
  CaptionTranslationSlotResult,
  CaptionTranslationTitle,
} from '@/shared/apis/editor/post-caption-translation';
import { buildCaptionsFromTranslationCues } from '@/shared/lib/captions/caption-translation-cues';
import { buildTitleTranslationEditPatch } from '@/shared/lib/captions/title-translation-edit';

import {
  isCaptionAsset,
  type CaptionAsset,
  type EditorStarterAsset,
} from '../../assets/assets';
import {
  isCaptionsItem,
  type CaptionsItem,
} from '../../items/captions/captions-item-type';
import type { EditorStarterItem } from '../../items/item-type';
import {
  isTextItem,
  isTitleTextItem,
  type TextItem,
  type TextSegment,
} from '../../items/text/text-item-type';
import { stringSeemsRightToLeft } from '../../utils/text/right-to-left';
import type { EditorState } from '../types';

/**
 * LTS 자막 번역 — 에디터 상태 순수 함수.
 *
 * 번역 결과 적용(`applyCaptionTranslations`)과 원문↔번역 전환(`setCaptionDisplayLanguage`)이
 * 모두 여기 있다. 전환은 서버를 부르지 않고 asset 안에 보관한 두 벌(`sourceCaptions`·
 * `translatedCaptions`)만 스왑한다.
 */

/** 지금 화면에 어느 쪽을 띄울지. */
export type CaptionDisplayLanguage = 'source' | 'translated';

/** 화면에 띄울 수 있는 번역 상태. `failed` 는 원문을 그대로 둔다. */
const DISPLAYABLE_TRANSLATION_STATUSES: ReadonlySet<string> = new Set([
  'translated',
  'passthrough',
]);

type CaptionWithSpeaker = Caption & { speaker?: string[] };

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * 캡션 asset 의 시간 기준점(절대 ms).
 *
 * 번역 요청의 단어 타이밍과 응답 cue 타이밍이 모두 이 값을 기준으로 상대화되므로,
 * 요청 생성과 결과 적용이 반드시 같은 함수를 쓴다.
 */
export const resolveCaptionAssetBaseMs = (asset: CaptionAsset): number => {
  if (isFiniteNumber(asset.videoStart)) return asset.videoStart;

  const firstCaption = (asset.sourceCaptions ?? asset.captions)[0];
  return isFiniteNumber(firstCaption?.startMs) ? firstCaption.startMs : 0;
};

/** 표시 중인 자막이 원문인지. `captionLanguage` 가 없는 구 asset 도 원문으로 본다. */
const isShowingSourceCaptions = (asset: CaptionAsset): boolean =>
  asset.captionLanguage === undefined || asset.captionLanguage === 'source';

/**
 * 지금 화면에 있는 `captions` 가 어느 언어인지. 원문이면 `'source'`.
 *
 * 구조 편집으로 보관본을 뗀 asset 은 이 값만으로 남은 한 벌의 언어를 알 수 있다.
 */
export const captionDisplayLanguageOf = (asset: CaptionAsset): string =>
  isShowingSourceCaptions(asset) ? 'source' : (asset.captionLanguage as string);

/** 현재 제목 표시 언어. 번역 메타가 없는 기존 title item 은 원문으로 본다. */
export const titleDisplayLanguageOf = (item: TextItem): string =>
  item.titleLanguage === undefined || item.titleLanguage === 'source'
    ? 'source'
    : item.titleLanguage;

export const hasStoredTitleTranslation = (item: EditorStarterItem): boolean =>
  isTextItem(item) &&
  isTitleTextItem(item) &&
  item.translatedTitle !== undefined;

/**
 * 자막 텍스트 편집분을 표시 중인 언어의 보관본에도 반영한다 (design.md §7-4).
 *
 * `captions` 만 갱신하면 원문↔번역을 왕복했을 때 보관본(`sourceCaptions`/`translatedCaptions`)으로
 * 되돌아가 편집이 사라진다. 편집 지점에서 이 패치를 asset 에 함께 펼쳐 넣는다.
 *
 * 번역본이 없는 asset(=대부분)에서는 빈 객체라 기존 동작과 완전히 동일하다.
 */
export const syncEditedCaptionsToStore = (
  asset: CaptionAsset,
  nextCaptions: Caption[],
): Partial<CaptionAsset> => {
  if (!asset.translatedCaptions) return {};
  return isShowingSourceCaptions(asset)
    ? { sourceCaptions: nextCaptions }
    : { translatedCaptions: nextCaptions };
};

/**
 * 제목 편집분을 현재 표시 중인 언어의 보관본에도 반영한다.
 *
 * 원문이 바뀌면 기존 번역은 더 이상 원문과 대응하지 않으므로 제거한다. 번역본을 편집할
 * 때는 대상 언어를 유지한 수정만 번역본으로 보관하고, 다른 언어로 바뀌면 새 원문으로
 * 재설정해 다시 번역할 수 있게 한다.
 */
export const syncEditedTitleToStore = (
  item: TextItem,
  nextText: string,
  nextSegments?: TextSegment[],
): Partial<TextItem> => {
  if (!isTitleTextItem(item)) return {};
  const editPatch = buildTitleTranslationEditPatch(
    {
      displayedText: item.text,
      sourceText: item.renderMeta.title,
      displayLanguage: item.titleLanguage,
      translatedLanguage: item.translatedLanguage,
      hasStoredTranslation: item.translatedTitle !== undefined,
    },
    nextText,
  );

  return {
    ...(editPatch.sourceText !== undefined
      ? {
          renderMeta: { ...item.renderMeta, title: editPatch.sourceText },
          sourceTitleSegments: nextSegments,
        }
      : {}),
    ...(editPatch.translatedText !== undefined
      ? {
          translatedTitle: editPatch.translatedText,
          translatedTitleSegments: nextSegments,
        }
      : {}),
    ...(editPatch.displayLanguage !== undefined
      ? { titleLanguage: editPatch.displayLanguage }
      : {}),
    ...(editPatch.clearStoredTranslation
      ? {
          translatedTitle: undefined,
          translatedLanguage: undefined,
          translatedTitleSegments: undefined,
        }
      : {}),
  };
};

const readItemDirection = (captions: Caption[]): CaptionsItem['direction'] =>
  stringSeemsRightToLeft(captions.map((c) => (c.text ?? '').trim()).join(' '))
    ? 'rtl'
    : 'ltr';

/** assetId → 그 asset 을 쓰는 caption item id 목록. */
const indexCaptionItemIdsByAssetId = (
  items: EditorState['undoableState']['items'],
): Map<string, string[]> => {
  const index = new Map<string, string[]>();
  Object.values(items).forEach((item) => {
    if (!isCaptionsItem(item)) return;
    const bucket = index.get(item.assetId);
    if (bucket) bucket.push(item.id);
    else index.set(item.assetId, [item.id]);
  });
  return index;
};

/**
 * 온디맨드 번역 요청 body 의 `slots` 를 만든다.
 *
 * 이미 같은 언어로 번역된 asset 은 제외해 누락분(생성 시 미번역·자막 확장으로 생긴 조각)만
 * 보낸다. 단어 타이밍은 asset 기준점(`resolveCaptionAssetBaseMs`)으로 상대화한다.
 */
export const buildCaptionTranslationSlots = (
  assets: Record<string, EditorStarterAsset>,
  targetLanguage: string,
): CaptionTranslationSlot[] => {
  const slots: Array<{ slot: CaptionTranslationSlot; baseMs: number }> = [];

  Object.values(assets).forEach((asset) => {
    if (!isCaptionAsset(asset)) return;
    if (asset.translatedLanguage === targetLanguage) return;
    // 구조 편집으로 보관본을 뗀 asset — 남은 한 벌이 이미 타겟 언어면 다시 번역하지 않는다.
    // (그대로 보내면 번역문을 또 번역해 표현만 흔들린다.)
    if (captionDisplayLanguageOf(asset) === targetLanguage) return;

    const baseMs = resolveCaptionAssetBaseMs(asset);
    const sourceCaptions = asset.sourceCaptions ?? asset.captions;

    const words = sourceCaptions
      .map((caption) => {
        const word = (caption.text ?? '').trim();
        if (!word) return null;
        const speaker = (caption as CaptionWithSpeaker).speaker;
        return {
          word,
          start: Math.round((caption.startMs ?? baseMs) - baseMs),
          end: Math.round((caption.endMs ?? baseMs) - baseMs),
          ...(Array.isArray(speaker) && speaker.length > 0 ? { speaker } : {}),
        };
      })
      .filter((word): word is NonNullable<typeof word> => word !== null);

    if (words.length === 0) return;

    slots.push({ slot: { key: asset.id, words }, baseMs });
  });

  // ai-api 가 인접 슬롯을 한 문장으로 이어 번역하므로 재생 순서대로 보낸다.
  return slots
    .sort((a, b) => a.baseMs - b.baseMs || a.slot.key.localeCompare(b.slot.key))
    .map((entry) => entry.slot);
};

/** 온디맨드 번역 요청 body 의 `titles` 를 재생 순서대로 만든다. */
export const buildTitleTranslationRequests = (
  items: Record<string, EditorStarterItem>,
  targetLanguage: string,
): CaptionTranslationTitle[] =>
  Object.values(items)
    .filter((item): item is TextItem => isTextItem(item))
    .filter((item) => isTitleTextItem(item))
    .filter(
      (item) =>
        item.translatedLanguage !== targetLanguage &&
        titleDisplayLanguageOf(item) !== targetLanguage,
    )
    .map((item) => ({
      item,
      text:
        item.translatedTitle === undefined ? item.text : item.renderMeta.title,
    }))
    .filter(({ text }) => text.trim().length > 0)
    .sort(
      (a, b) => a.item.from - b.item.from || a.item.id.localeCompare(b.item.id),
    )
    .map(({ item, text }) => ({ key: item.id, text }));

/**
 * 구조 편집(트림·분할·병합·축소) 결과 asset 에서 번역 보관본을 떼어 낸다 (design.md §7-4).
 *
 * 이 편집들은 asset 의 시간 범위를 바꾸거나 asset 을 새로 쪼개므로, 보관해 둔 반대편 배열
 * (`sourceCaptions`/`translatedCaptions`)이 더는 그 범위와 맞지 않는다. 그대로 두면 원문↔번역 전환이
 * 편집 전 자막을 되살려 편집분을 조용히 지운다. 그래서 화면에 있는 `captions` 한 벌만 남기고
 * 보관본·복원 정보를 버린다 — 그 asset 은 [번역] 이 다시 처리할 미번역분이 된다.
 *
 * 번역본이 없는 asset(=대부분)에서는 받은 asset 을 그대로 돌려주므로 기존 동작과 완전히 동일하다.
 */
export const withoutCaptionTranslation = (
  asset: CaptionAsset,
): CaptionAsset => {
  if (!asset.translatedCaptions) return asset;
  const {
    sourceCaptions: _sourceCaptions,
    translatedCaptions: _translatedCaptions,
    translatedLanguage: _translatedLanguage,
    sourceHighlight: _sourceHighlight,
    ...rest
  } = asset;
  // captionLanguage 는 지금 화면에 있는 언어 그대로 둔다 — 남은 한 벌이 그 언어이기 때문.
  return rest;
};

/**
 * 번역 결과를 상태에 적용한다.
 *
 * asset 에 두 벌(원문·번역)을 넣고 표시본을 번역으로 바꾸며, 해당 item 의 하이라이트를 끈다
 * (번역문은 단어 타이밍이 없어 켜 두면 문장 전체가 강조색이 된다). 끄기 전 값은 asset 의
 * `sourceHighlight` 에 보관해 원문으로 돌아갈 때 복원한다. `failed` 슬롯은 원문을 유지한다.
 */
export const applyCaptionTranslations = (
  state: EditorState,
  results: readonly CaptionTranslationSlotResult[],
  targetLanguage: string,
): EditorState => {
  if (results.length === 0) return state;

  const assets = { ...state.undoableState.assets };
  const items = { ...state.undoableState.items };
  const itemIdsByAssetId = indexCaptionItemIdsByAssetId(items);
  let hasChange = false;

  results.forEach((result) => {
    const asset = assets[result.key];
    if (!isCaptionAsset(asset)) return;

    const translation = result.translation;
    if (!translation) return;
    if (!DISPLAYABLE_TRANSLATION_STATUSES.has(translation.status)) return;

    const translatedCaptions = buildCaptionsFromTranslationCues({
      cues: translation.cues,
      baseMs: resolveCaptionAssetBaseMs(asset),
    });
    if (translatedCaptions.length === 0) return;

    const language = translation.targetLanguage || targetLanguage;
    const itemIds = itemIdsByAssetId.get(asset.id) ?? [];
    const firstItem = items[itemIds[0] ?? ''];
    const sourceHighlight =
      asset.sourceHighlight ??
      (isCaptionsItem(firstItem)
        ? {
            highlightVisible: firstItem.highlightVisible,
            highlightEffect: firstItem.highlightEffect,
          }
        : undefined);

    assets[asset.id] = {
      ...asset,
      captions: translatedCaptions,
      captionLanguage: language,
      sourceCaptions: asset.sourceCaptions ?? asset.captions,
      translatedCaptions,
      translatedLanguage: language,
      ...(sourceHighlight ? { sourceHighlight } : {}),
    };

    const direction = readItemDirection(translatedCaptions);
    itemIds.forEach((itemId) => {
      const item = items[itemId];
      if (!isCaptionsItem(item)) return;
      items[itemId] = {
        ...item,
        highlightVisible: false,
        highlightEffect: 'instant',
        direction,
      };
    });

    hasChange = true;
  });

  if (!hasChange) return state;

  return {
    ...state,
    undoableState: { ...state.undoableState, assets, items },
  };
};

/** 성공한 제목 번역만 적용한다. 응답에서 누락된 제목은 원문 상태를 그대로 유지한다. */
export const applyTitleTranslations = (
  state: EditorState,
  results: readonly CaptionTranslationTitle[],
  targetLanguage: string,
): EditorState => {
  if (results.length === 0) return state;

  const items = { ...state.undoableState.items };
  let hasChange = false;

  results.forEach((result) => {
    if (result.text.trim().length === 0) return;

    const item = items[result.key];
    if (!item || !isTextItem(item) || !isTitleTextItem(item)) return;

    const isFirstTranslation = item.translatedTitle === undefined;
    const sourceTitle = isFirstTranslation ? item.text : item.renderMeta.title;
    const sourceTitleSegments = isFirstTranslation
      ? item.segments
      : item.sourceTitleSegments;

    items[item.id] = {
      ...item,
      text: result.text,
      segments: undefined,
      direction: stringSeemsRightToLeft(result.text) ? 'rtl' : 'ltr',
      renderMeta: { ...item.renderMeta, title: sourceTitle },
      translatedTitle: result.text,
      translatedLanguage: targetLanguage,
      titleLanguage: targetLanguage,
      sourceTitleSegments,
      translatedTitleSegments: undefined,
    };
    hasChange = true;
  });

  if (!hasChange) return state;

  return {
    ...state,
    undoableState: { ...state.undoableState, items },
  };
};

/**
 * 표시 언어를 원문↔번역으로 스왑한다(서버 호출 없음).
 *
 * 번역본이 없는 asset 은 건드리지 않는다. 원문으로 돌아갈 때는 asset 에 보관한
 * `sourceHighlight` 로 item 하이라이트를 복원한다. targetLanguage가 주어지면 다른
 * 언어의 구번역본은 올리지 않고 원문에 둔다.
 */
export const setCaptionDisplayLanguage = (
  state: EditorState,
  language: CaptionDisplayLanguage,
  targetLanguage?: string,
): EditorState => {
  const assets = { ...state.undoableState.assets };
  const items = { ...state.undoableState.items };
  const itemIdsByAssetId = indexCaptionItemIdsByAssetId(items);
  let hasChange = false;

  Object.values(state.undoableState.assets).forEach((asset) => {
    if (!isCaptionAsset(asset)) return;

    const translatedCaptions = asset.translatedCaptions;
    if (!translatedCaptions || translatedCaptions.length === 0) return;

    const showSource =
      language === 'source' ||
      (targetLanguage !== undefined &&
        asset.translatedLanguage !== targetLanguage);
    if (showSource === isShowingSourceCaptions(asset)) return;

    const sourceCaptions = asset.sourceCaptions;
    if (showSource && (!sourceCaptions || sourceCaptions.length === 0)) return;

    const nextCaptions = showSource
      ? (sourceCaptions ?? [])
      : translatedCaptions;
    const itemIds = itemIdsByAssetId.get(asset.id) ?? [];
    // 번역으로 넘어갈 때만 item 의 현재(원문) 하이라이트를 보관한다.
    // 원문으로 돌아갈 때 item 값은 이미 꺼진 상태라 asset 에 보관한 값만 신뢰한다.
    const firstItem = items[itemIds[0] ?? ''];
    const sourceHighlight = showSource
      ? asset.sourceHighlight
      : (asset.sourceHighlight ??
        (isCaptionsItem(firstItem)
          ? {
              highlightVisible: firstItem.highlightVisible,
              highlightEffect: firstItem.highlightEffect,
            }
          : undefined));

    assets[asset.id] = {
      ...asset,
      captions: nextCaptions,
      captionLanguage: showSource
        ? 'source'
        : (asset.translatedLanguage ?? 'translated'),
      ...(sourceHighlight ? { sourceHighlight } : {}),
    };

    const direction = readItemDirection(nextCaptions);
    itemIds.forEach((itemId) => {
      const item = items[itemId];
      if (!isCaptionsItem(item)) return;
      items[itemId] = {
        ...item,
        direction,
        ...(showSource
          ? // 보관된 원문 하이라이트가 없으면 item 값을 건드리지 않는다.
            (sourceHighlight ?? {})
          : { highlightVisible: false, highlightEffect: 'instant' as const }),
      };
    });

    hasChange = true;
  });

  Object.values(state.undoableState.items).forEach((originalItem) => {
    if (!isTextItem(originalItem) || !isTitleTextItem(originalItem)) return;
    if (!hasStoredTitleTranslation(originalItem)) return;

    const showSource =
      language === 'source' ||
      (targetLanguage !== undefined &&
        originalItem.translatedLanguage !== targetLanguage);
    if (showSource === (titleDisplayLanguageOf(originalItem) === 'source')) {
      return;
    }

    const currentItem = items[originalItem.id];
    if (!currentItem || !isTextItem(currentItem)) return;

    const nextText = showSource
      ? originalItem.renderMeta.title
      : (originalItem.translatedTitle ?? '');

    items[originalItem.id] = {
      ...currentItem,
      text: nextText,
      segments: showSource
        ? originalItem.sourceTitleSegments
        : originalItem.translatedTitleSegments,
      direction: stringSeemsRightToLeft(nextText) ? 'rtl' : 'ltr',
      titleLanguage: showSource
        ? 'source'
        : (originalItem.translatedLanguage ?? 'translated'),
    };
    hasChange = true;
  });

  if (!hasChange) return state;

  return {
    ...state,
    undoableState: { ...state.undoableState, assets, items },
  };
};
